import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Layers, Filter, CalendarX2, Users, ClipboardCheck, CalendarDays } from "lucide-react";
import { AppShell } from "@/components/shell/AppShell";
import { ModuleHeader, FeedChips, Toolbar, PillSelect, Segmented, EmptyState, Loading } from "@/components/kit";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { filterData, presetsFor, monthLabel, monthsOf, setBasis, ACCENT, GROUPS, SITES } from "./qUtils";
import QAgentAudit from "./QAgentAudit";
import QEvaluatorAudit from "./QEvaluatorAudit";
import { QAgentDrawer, QEvaluatorDrawer } from "./QDrawers";

const VIEW_OF = { exec: "agent", tl: "agent", params: "agent", evaluators: "auditor", agent: "agent", auditor: "auditor" };
const TAB_OF = { agent: ["monthly", "weekly", "summary", "teams", "compare"], auditor: ["mom", "compare", "timing"] };
const monthEnd = (ym) => { const [y, m] = ym.split("-").map(Number); return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`; };
const ViewCard = ({ id, active, icon: Icon, title, sub, count, onClick }) => (
  <button data-testid={`q-view-card-${id}`} onClick={onClick} className={`mk-card flex items-center gap-4 p-5 text-left transition-colors ${active ? "border-thamber/60 bg-thamber/10" : "mk-card-hover"}`}>
    <span className={`mk-icon-tile h-11 w-11 rounded-xl ${active ? "bg-thamber text-white" : "bg-thamber/15 text-thamber"}`}><Icon className="h-5 w-5" /></span>
    <div className="min-w-0 flex-1"><p className="font-heading text-lg font-bold text-thstrong">{title}</p><p className="truncate text-xs text-thmute">{sub}</p></div>
    <span className="font-heading text-2xl font-extrabold" style={{ color: active ? "rgb(var(--th-amber))" : "rgb(var(--th-dim))" }}>{count}</span>
  </button>
);

export default function QualityPage() {
  const { tenantId, tenant } = useTenant() || {};
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(VIEW_OF[params.get("view")] || "agent");
  const [tab, setTab] = useState(params.get("tab") || "");
  const [month, setMonth] = useState(params.get("month") || "");
  const [group, setGroup] = useState(params.get("group") || "lob");
  const [value, setValue] = useState(params.get("value") || "All");
  const [basis, setBasisState] = useState(params.get("basis") || "interaction");
  const [site, setSite] = useState(params.get("site") || "All");
  setBasis(basis);
  const [agent, setAgent] = useState(params.get("agent") || null);
  const [evaluator, setEvaluator] = useState(null);

  const load = useCallback((refresh = false) => {
    if (!tenantId) { setError("No client selected. Use the client switcher in the header."); return; }
    setBusy(true); setError(null);
    api.get(`/modules/quality/dashboard?tenant_id=${tenantId}&refresh=${refresh}`)
      .then((r) => {
        setData(r.data);
        setBasis(new URLSearchParams(window.location.search).get("basis") || "interaction");
        setMonth((m) => m || presetsFor(r.data).month);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  }, [tenantId]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const next = {};
    if (view !== "agent") next.view = view;
    if (tab) next.tab = tab;
    if (month) next.month = month;
    if (group !== "lob") next.group = group;
    if (value !== "All") next.value = value;
    if (agent) next.agent = agent;
    if (basis !== "interaction") next.basis = basis;
    if (site !== "All") next.site = site;
    setParams(next, { replace: true });
  }, [view, tab, month, group, value, agent, basis, site, setParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const presets = useMemo(() => (data ? presetsFor(data) : null), [data, basis]);
  const values = useMemo(() => {
    if (!data) return [];
    const cnt = {};
    data.evals.forEach((e) => { if (site === "All" || e.loc === site) cnt[e[group]] = (cnt[e[group]] || 0) + 1; });
    return Object.entries(cnt).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [data, group, site]);
  useEffect(() => { if (value !== "All" && values.length && !values.includes(value)) setValue("All"); }, [values, value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const all = useMemo(() => (data ? filterData(data, { from: "0000", to: "9999", group, value, site }).evals : []), [data, group, value, site, basis]);
  const months = useMemo(() => monthsOf(all), [all]);
  const curMonth = month === "all" ? "all" : months.includes(month) ? month : months[months.length - 1] || "all";
  const range = curMonth === "all" ? { from: presets?.first || "0000", to: presets?.latest || "9999" } : { from: `${curMonth}-01`, to: monthEnd(curMonth) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slice = useMemo(() => (data && presets ? filterData(data, { ...range, group, value, site }) : null), [data, range.from, range.to, group, value, site, basis]);
  const curTab = TAB_OF[view].includes(tab) ? tab : TAB_OF[view][0];
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const ctx = { data, slice, all, months, month: curMonth, range, basis, group, value, tenantId, tab: curTab, setTab, onOpenAgent: setAgent, onOpenEvaluator: setEvaluator };

  return (
    <AppShell wide>
      <ModuleHeader testid="q-title" title="Quality" subtitle="QA & Calibration" accent={ACCENT}
        context={`${tenant?.name || "No client"} · Workspace data · monthly uploads`}
        meta={data ? `Snapshot · ${(data.months_loaded || []).length} months · ${data.evals.length.toLocaleString()} evaluations${data.skipped ? ` · ${data.skipped} rows skipped` : ""}${data.duplicates ? ` · ${data.duplicates} duplicates merged` : ""}` : null}
        onRefresh={data ? () => load(true) : null} busy={busy} />

      {error ? (
        <EmptyState testid="q-error" title="No data for this module" body={error} isAdmin={isAdmin} action={{ to: "/settings?section=uploads", label: "Upload monthly data" }} />
      ) : !data || !slice ? <Loading /> : (
        <>
          <FeedChips testid="q-coverage-banner" coverage={data.coverage} months={data.months_loaded || []}
            hint={data.has_fatal ? "Fatal flag detected from Rating / critical column" : "No fatal-error column in uploads"} />
          <p data-testid="q-span-caption" className="mk-sub -mt-2 mb-4">
            {basis === "interaction" ? "Interaction basis" : "Evaluation basis"} · evaluations span {monthLabel(presets.first.slice(0, 7))} → {monthLabel(presets.month)}
            {data.no_interaction_date ? ` · ${data.no_interaction_date.toLocaleString()} rows without interaction date fall back to evaluation date` : ""}
          </p>
          <Toolbar testid="q-toolbar">
            <PillSelect testid="q-month-select" icon={CalendarDays} value={curMonth} onChange={(e) => setMonth(e.target.value)}>
              <option value="all">All months</option>
              {[...months].reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </PillSelect>
            <span className="mk-sub hidden lg:inline">KPIs follow the month · trends show every uploaded month</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5"><span className="mk-sub">Date basis</span>
                <Segmented testidPrefix="q-basis" options={["Interaction", "Evaluation"]} value={basis === "interaction" ? "Interaction" : "Evaluation"} onChange={(v) => setBasisState(v.toLowerCase())} /></div>
              <Segmented testidPrefix="q-site" options={SITES} value={site} onChange={setSite} />
              <PillSelect testid="q-group-select" icon={Layers} value={group} onChange={(e) => { setGroup(e.target.value); setValue("All"); }}>
                {GROUPS.map(([k, l]) => <option key={k} value={k}>Group by {l}</option>)}
              </PillSelect>
              <PillSelect testid="q-value-select" icon={Filter} value={value} onChange={(e) => setValue(e.target.value)}>
                <option value="All">All {GROUPS.find(([k]) => k === group)[1]}s</option>
                {values.map((v) => <option key={v} value={v}>{v}</option>)}
              </PillSelect>
            </div>
          </Toolbar>
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="q-view-cards">
            <ViewCard id="agent" active={view === "agent"} icon={Users} title="Agent Audit" sub="Monthly · Weekly · Summary · Team Comparison · Comparison" count={new Set(slice.evals.map((e) => e.c)).size} onClick={() => { setView("agent"); setTab(""); }} />
            <ViewCard id="auditor" active={view === "auditor"} icon={ClipboardCheck} title="Evaluator Audit" sub="Month-on-Month · Comparison · Audit Timing & Submission Pattern" count={new Set(slice.evals.map((e) => e.ev)).size} onClick={() => { setView("auditor"); setTab(""); }} />
          </div>

          {slice.evals.length === 0 ? (
            <EmptyState testid="q-empty-range" icon={CalendarX2} title="No PMT rows in this range" body="Widen the date range or clear the group filter to see evaluations." />
          ) : (
            <>
              {view === "agent" && <QAgentAudit {...ctx} />}
              {view === "auditor" && <QEvaluatorAudit {...ctx} />}
            </>
          )}
          {agent && <QAgentDrawer data={data} cisco={agent} onClose={() => setAgent(null)} />}
          {evaluator && <QEvaluatorDrawer data={data} slice={slice} name={evaluator} onClose={() => setEvaluator(null)} />}
        </>
      )}
    </AppShell>
  );
}
